/**
 * Editor для doc.settlement_inventories (Фаза 2.1 #4).
 * From-registers + items table з confirmed_amount edit + variance.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import { settlementInventories } from '@/api/settlements';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { extractApiError } from '@/utils/extractApiError';
import DatePopover from '@/components/DatePopover';
import RequiredLabel from '@/components/RequiredLabel';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useConfirm } from '@/components/ConfirmProvider';
import TchTable from '@/shared/components/TchTable';

const PERMISSION = 'documents.settlement_inventories.create';

interface FormState {
  organization_id: string;
  document_date: string;
  inventory_date: string;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_date: new Date().toISOString().slice(0, 16),
  inventory_date: new Date().toISOString().slice(0, 10),
  comment: '',
};

const STATE_LABEL: Record<string, string> = {
  IN_PROGRESS: 'У процесі', COMPLETED: 'Завершено',
};

export default function SettlementInventoryEditor() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const { availableOrgs } = usePermissionsByOrg(PERMISSION);
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const docQuery = useQuery({
    queryKey: ['settlement-inventory', id],
    queryFn: () => settlementInventories.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_date: d.document_date.slice(0, 16),
        inventory_date: d.inventory_date,
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;

  // ч.703: видалення DRAFT (кнопка на плашці; new/posted — disabled).
  const remove = useMutation({
    mutationFn: () => settlementInventories.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settlement-inventories'] });
      navigate('/settlements/settlement-inventories');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const fromRegisters = useMutation({
    mutationFn: () => settlementInventories.fromRegisters({
      organization_id: form.organization_id,
      inventory_date: form.inventory_date,
      document_date: new Date(form.document_date).toISOString(),
      comment: form.comment || null,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['settlement-inventories'] });
      navigate(`/settlements/settlement-inventories/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const updateItem = useMutation({
    mutationFn: (p: { itemId: string; confirmedAmount: string; reason?: string }) =>
      settlementInventories.updateItem(id!, p.itemId, {
        confirmed_amount: p.confirmedAmount,
        variance_reason: p.reason || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settlement-inventory', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  const postDoc = useMutation({
    mutationFn: () => settlementInventories.post(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settlement-inventory', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  // 2026-08-12 (доводка): «підтверджено» з узгоджених актів звірки —
  // заповнює лише порожні клітинки (ручні значення не перетирає).
  const pullActs = useMutation({
    mutationFn: () => settlementInventories.pullConfirmedActs(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settlement-inventory', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  // ч.917 (новий дизайн, Закон #10): «ще дії» переїхали у праву колонку
  // (Повʼязані дії) — той самий набір, що був у extraActions тулбара.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [{
    label: 'підтягнути з актів звірки',
    title: isNew ? 'Спершу збережіть документ'
      : isPosted ? 'Документ проведено — колонка зафіксована'
      : 'Заповнити колонку «Підтверджено» з узгоджених актів звірки (лише порожні рядки)',
    disabled: pullActs.isPending || isNew || isPosted,
    onClick: () => pullActs.mutate(),
  }];
  // ч.917: блок рядків є лише у збереженого документа — нумерація з масиву.
  const itemsCount = docQuery.data?.items.length ?? 0;
  const itemsTitle = `Рядки (${itemsCount})`;
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 1,
      filled: form.organization_id ? 1 : 0 },
    ...(!isNew && docQuery.data ? [{ id: 'items', title: itemsTitle, required: 0, filled: 0 }] : []),  // ч.917: рядки генеруються з регістрів, не обовʼязкове поле
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];
  const num = (bid: string) => blocks.findIndex((b) => b.id === bid) + 1;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова інвентаризація розрахунків' : `Інвентаризація ${docQuery.data?.document_number}`}
        </h2>
        {/* ч.641: канон DocumentToolbar — плашка дій + бейджі стану в рядку назви. */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          railMode
          onBackToList={() => navigate('/settlements/settlement-inventories')}
          savable={isNew}
          saveLabel="згенерувати з реєстрів"
          onSave={() => fromRegisters.mutate()}
          savePending={fromRegisters.isPending}
          saveDisabled={!form.organization_id}
          isNew={isNew}
          isPosted={isPosted}
          onPost={() => postDoc.mutate()}
          // ч.1266: новий документ спершу формується кнопкою «зберегти» (з регістрів)
          postDisabled={isNew}
          postTitle={isNew ? 'Спершу збережіть документ' : undefined}
          postPending={postDoc.isPending}
          onDelete={() => { void confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          entriesDoc={{ type: 'doc.settlement_inventories', id: isNew ? '' : id! }}
          extraStatusBadges={!isNew ? [{
            label: (STATE_LABEL[docQuery.data?.state ?? 'IN_PROGRESS'] ?? '').toLowerCase(),
            tone: docQuery.data?.state === 'COMPLETED' ? 'green' : 'amber',
          }] : []}
          extraActions={railActions}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.settlement_inventories', id: isNew ? '' : id! }} />
      }>
      {/* ч.917 — блоками (Закон #10); сітка полів — як була. */}
      <DocBlock index={num('main')} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <RequiredLabel>Юрособа</RequiredLabel>
          {isNew ? (
            <select value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}>
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
              ))}
            </select>
          ) : (
            <div>{orgList.data?.items.find((o) => o.id === form.organization_id)?.name ?? form.organization_id}</div>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата документа</label>
          <DatePopover withTime value={form.document_date}
            disabled={!isNew} onChange={(v) => setForm({ ...form, document_date: v })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата інвентаризації</label>
          <DatePopover value={form.inventory_date}
            disabled={!isNew} onChange={(v) => setForm({ ...form, inventory_date: v })} />
        </div>
      </div>
      </DocBlock>

      {!isNew && docQuery.data && (
        <DocBlock index={num('items')} title={itemsTitle}>
          <TchTable>
            <thead>
              <tr>
                <th>#</th>
                <th>Контрагент</th>
                <th>Договір</th>
                <th>Книжкова</th>
                <th>Підтверджено</th>
                <th>Розбіжність</th>
                <th>Причина</th>
              </tr>
            </thead>
            <tbody>
              {docQuery.data.items.map((it) => (
                <tr key={it.id}>
                  <td>{it.line_number}</td>
                  <td>{contractorsQ.data?.items.find((c) => c.id === it.contractor_id)?.name
                    ?? it.contractor_id.slice(0, 8) + '…'}</td>
                  <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{it.contract_id ? 'за договором' : 'всі'}</td>
                  <td style={{ textAlign: 'right' }}>{Number(it.book_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })}</td>
                  <td>
                    <input
                      type="number" step="0.0001"
                      defaultValue={it.confirmed_amount ?? ''}
                      disabled={isPosted}
                      onBlur={(e) => {
                        if (e.target.value && e.target.value !== it.confirmed_amount) {
                          updateItem.mutate({ itemId: it.id, confirmedAmount: e.target.value });
                        }
                      }}
                      style={{ width: '100px', textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: parseFloat(it.variance_amount) === 0 ? '#15803d' : '#b91c1c' }}>
                    {Number(it.variance_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })}
                  </td>
                  <td>
                    <input defaultValue={it.variance_reason ?? ''} disabled={isPosted}
                      placeholder="причина розбіжності"
                      onBlur={(e) => {
                        if ((e.target.value || null) !== (it.variance_reason ?? null)) {
                          updateItem.mutate({
                            itemId: it.id,
                            confirmedAmount: it.confirmed_amount ?? '',
                            reason: e.target.value,
                          });
                        }
                      }}
                      style={{ width: '100%', fontSize: '0.85rem' }} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 'bold' }}>
                <td colSpan={3}>Всього</td>
                <td style={{ textAlign: 'right' }}>{Number(docQuery.data.total_book_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })}</td>
                <td style={{ textAlign: 'right' }}>{Number(docQuery.data.total_confirmed_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })}</td>
                <td style={{ textAlign: 'right', color: parseFloat(docQuery.data.total_variance_amount) === 0 ? '#15803d' : '#b91c1c' }}>
                  {Number(docQuery.data.total_variance_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </TchTable>
        </DocBlock>
      )}

      <DocBlock index={num('extra')} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          {/* ч.917: поля коментаря на формі не було (значення вже йшло у
              «згенерувати з реєстрів»); редагується лише у нового документа —
              окремого збереження шапки документ не має. */}
          <input value={form.comment} disabled={!isNew}
            onChange={(e) => setForm({ ...form, comment: e.target.value })} />
        </div>
        <div>
          {/* ч.917: вкладення (рішення власника 2026-08-13: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.settlement_inventories" documentId={isNew ? undefined : id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>

      {error && <div className="error" style={{ marginTop: '1rem', color: 'var(--badge-red-fg)' }}>{error}</div>}
    </div>
  );
}
